import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { DgaIcon } from "platformscode-new-react";
const logo     = "/assets2/logos/hmm-logo-02.png";
const logoWebp = "/assets2/logos/hmm-logo-02-compressed.webp";
const logoAvif = "/assets2/logos/hmm-logo-nav.avif";
import MenuItem from "./MenuItem";
import NavigationSubmenu from "./NavigationSubmenu";
import MobileNavbar from "../MobileNav";
import { MENU_DATA, ACTION_ITEMS, resolveActiveId } from "./menuData";
import useHorizontalScroll from "./useHorizontalScroll";
import "./NavHeader.css";

// Scroll button used on both sides of the menu list.
const ScrollButton = ({ direction, onClick, label }) => (
  <button
    type="button"
    className={`scroll-button scroll-button--${direction}`}
    onClick={onClick}
    aria-label={label}
    tabIndex={-1}
  >
    <span aria-hidden="true">{direction === "left" ? "‹" : "›"}</span>
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// Props:
//   isMobileMenuOpen   — whether the mobile drawer is open (from Header.jsx)
//   onToggleMobileMenu — callback to toggle the mobile drawer
//   isViewGreyscale    — apply greyscale filter for accessibility mode
// ─────────────────────────────────────────────────────────────────────────────
function NavHeader({ isMobileMenuOpen, onToggleMobileMenu, onCloseMobileMenu, isViewGreyscale }) {
  const { pathname, search } = useLocation();

  const [activeLink,    setActiveLink]    = useState(() => resolveActiveId(pathname, search));
  const [openSubmenuId, setOpenSubmenuId] = useState(null);

  const navRef        = useRef(null);
  const menuScrollRef = useRef(null);

  const { canScrollLeft, canScrollRight, scrollLeft, scrollRight } =
    useHorizontalScroll(menuScrollRef);

  // Set --mobile-nav-top to the nav bar's actual bottom position in the viewport.
  // This means the mobile menu always opens directly below the nav bar — whether
  // the nav is sticky at top:0 or still in its natural position at the top of the page.
  // Updated on both resize (ResizeObserver) and scroll so the value stays accurate.
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const update = () => {
      const bottom = el.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty("--mobile-nav-top", `${bottom}px`);
      // Toggle class used by MobileNav.css to switch height at 72px scroll threshold
      document.documentElement.classList.toggle("nav-is-sticky", window.scrollY > 72);
    };
    update(); // set initial value synchronously
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("scroll", update, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update);
    };
  }, []);

  // Sync active link and close any open submenu on route change.
  useEffect(() => {
    setActiveLink(resolveActiveId(pathname, search));
    setOpenSubmenuId(null);
  }, [pathname, search]);

  // Close submenu on outside click or Escape key.
  useEffect(() => {
    const close = () => {
      setOpenSubmenuId(null);
      setActiveLink(resolveActiveId(pathname, search));
    };
    const onMouseDown = (e) => { if (!navRef.current?.contains(e.target)) close(); };
    const onKeyDown   = (e) => { if (e.key === "Escape") close(); };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown",   onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown",   onKeyDown);
    };
  }, [pathname]);

  const toggleSubmenu = useCallback((id) => {
    setOpenSubmenuId((prev) => {
      const opening = prev !== id;
      setActiveLink(opening ? id : resolveActiveId(pathname));
      return opening ? id : null;
    });
  }, [pathname]);

  const handleLinkClick = useCallback((id) => {
    setActiveLink(id);
    setOpenSubmenuId(null);
  }, []);

  // Language switching is handled by the Google Translate system in index.html
  // via the "translate-btn" class on the button — no direct DOM manipulation needed.
  const handleTranslate = useCallback(() => {}, []);

  const stickyClass = useMemo(() =>
    ["nav-sticky-wrapper",
      isViewGreyscale && "greyscale",
    ].filter(Boolean).join(" "),
  [isMobileMenuOpen, isViewGreyscale]);

  const menuWrapperClass = useMemo(() =>
    ["header-nav__menu-wrapper",
      canScrollLeft  && "has-scroll-left",
      canScrollRight && "has-scroll-right",
    ].filter(Boolean).join(" "),
  [canScrollLeft, canScrollRight]);

  const activeSubmenu = useMemo(
    () => MENU_DATA.find((item) => item.id === openSubmenuId),
    [openSubmenuId],
  );

  return (
    <div ref={navRef} className={stickyClass}>

      <header className="header header--divider">
        <nav className="header-nav--full" aria-label="القائمة الرئيسية">

          <div className="header-nav__main">

            {/* Hamburger — mobile only */}
            <div className="header-menu__btn">
              <button
                type="button"
                aria-label={isMobileMenuOpen ? "إغلاق قائمة التنقل" : "فتح قائمة التنقل"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-nav"
                onClick={onToggleMobileMenu}
              >
                <DgaIcon
                  icon={isMobileMenuOpen ? "cancel-01" : "menu-01"}
                  variant="stroke"
                  type="rounded"
                  size={24}
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Logo */}
            <div className="header-nav__branding">
              <Link
                to="/"
                className="header__logo"
                aria-label="الصفحة الرئيسية - أمانة العاصمة المقدسة"
                onClick={() => handleLinkClick("home")}
              >
                <picture>
                  <source srcSet={logoAvif} type="image/avif" />
                  <source srcSet={logoWebp} type="image/webp" />
                  <img src={logo} alt="شعار أمانة العاصمة المقدسة" width="192" height="36" />
                </picture>
              </Link>
            </div>

            {/* Search button — mobile/tablet only */}
            <div className="header-search__btn">
              <Link
                to="/search-page"
                className="header-search__link"
                aria-label="البحث"
                onClick={() => handleLinkClick("search")}
              >
                <DgaIcon icon="search-01" variant="stroke" type="rounded" size={24} aria-hidden="true" />
              </Link>
            </div>

            {/* Desktop nav */}
            <div className={menuWrapperClass}>
              {canScrollLeft  && <ScrollButton direction="left"  onClick={scrollLeft}  label="تمرير القائمة يساراً" />}

              <ul className="header-nav__menu" ref={menuScrollRef} role="list" aria-label="روابط التنقل الرئيسية">
                <li className="scroll-sentinel-start" aria-hidden="true" />
                {MENU_DATA.map((item) => (
                  <MenuItem
                    key={item.id}
                    item={item}
                    isActive={activeLink === item.id}
                    isSubmenuOpen={openSubmenuId === item.id}
                    onToggleSubmenu={() => toggleSubmenu(item.id)}
                    onLinkClick={() => handleLinkClick(item.id)}
                  />
                ))}
                <li className="scroll-sentinel-end" aria-hidden="true" />
              </ul>

              {canScrollRight && <ScrollButton direction="right" onClick={scrollRight} label="تمرير القائمة يميناً" />}
            </div>
          </div>

          {/* Action buttons */}
          <ul className="header-nav__actions" role="list" aria-label="أدوات الموقع">
            {ACTION_ITEMS.map((action) => {
              const isActive  = activeLink === action.id;
              const className = `header-menu__item${isActive ? " header-menu__item--active" : ""}`;
              const children  = (
                <>
                  {action.label && <span className="header-menu__item-label" aria-hidden="true">{action.label}</span>}
                  <span className="header-menu__item-icon" aria-hidden="true">
                    <DgaIcon icon={action.icon} variant="stroke" type="rounded" size={16} tabIndex={-1} />
                  </span>
                </>
              );

              return (
                <li key={action.id} className={action.className || undefined}>
                  {action.isLanguageToggle ? (
                    <button id="translate-btn-desktop" type="button" className={className} onClick={handleTranslate} aria-label={action.label} lang="en">
                      {children}
                    </button>
                  ) : (
                    <Link to={action.href} className={className} aria-label={action.label} aria-current={isActive ? "page" : undefined} onClick={() => handleLinkClick(action.id)}>
                      {children}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

        </nav>
      </header>

      <NavigationSubmenu
        isOpen={!!activeSubmenu?.submenuColumns}
        columns={activeSubmenu?.submenuColumns ?? null}
        submenuId={activeSubmenu?.id ?? null}
        onLinkClick={() => activeSubmenu && handleLinkClick(activeSubmenu.id)}
      />

      <MobileNavbar
        isOpen={isMobileMenuOpen}
        closeMenu={onCloseMobileMenu}
      />

    </div>
  );
}

export default NavHeader;
